// src/hooks/useListingActions.js
import { useNavigate } from 'react-router-dom';
import {useState} from 'react';
import { useAuth } from '../context/AuthContext';
import ConfirmationModal from '../components/common/ConfirmationModal';
import {BASE_URL} from '../services/api';

export const useListingActions = (fetchData) => {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  // State for modals
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: '',
    cancelText: 'Cancel',
    confirmColor: 'primary'
  });

  const authenticatedApiCall = async (url, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${BASE_URL}${url}`, { ...options, headers });
      const responseText = await response.text();
      
      if (!responseText) {
        throw new Error('Empty response from server');
      }
      
      const data = JSON.parse(responseText);
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }
      
      return data;
    } catch (error) {
      // console.error('❌ API Error:', error);
      throw error;
    }
  };

  const showConfirmation = (config) => {
    setModalConfig({
      ...config,
      isOpen: true
    });
  };

  const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  };

  const handleEditListing = (listingId) => {
    navigate(`/edit-listing/${listingId}`);
  };

  const handleMarkAsSold = (listingId) => {
    showConfirmation({
      title: 'Mark as Sold',
      message: 'Are you sure you want to mark this listing as sold? This will notify any pending buyers.',
      confirmText: 'Mark as Sold',
      onConfirm: async () => {
        try {
          await authenticatedApiCall(`/my-listings/${listingId}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status: 'sold' }),
          });
          
          // Show success message (you could add toast notification here)
          // console.log('Success: Listing marked as sold');
          fetchData();
          closeModal();
        } catch (err) {
          // console.error('❌ Mark as sold error:', err);
          alert('Failed to update listing: ' + err.message);
        }
      }
    });
  };

  const handleReactivate = (listingId) => {
    showConfirmation({
      title: 'Reactivate Listing',
      message: 'Are you sure you want to reactivate this listing? It will become visible to buyers again.',
      confirmText: 'Reactivate',
      onConfirm: async () => {
        try {
          await authenticatedApiCall(`/my-listings/${listingId}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status: 'active' }),
          });
          
          // console.log('Success: Listing reactivated');
          fetchData();
          closeModal();
        } catch (err) {
          // console.error('❌ Reactivate error:', err);
          alert('Failed to reactivate listing: ' + err.message);
        }
      }
    });
  };

  const handleDeleteListing = (listingId, title) => {
    showConfirmation({
      title: 'Delete Listing',
      message: `Are you sure you want to delete "${title}"? This action cannot be undone and will remove all associated data.`,
      confirmText: 'Delete',
      confirmColor: 'red',
      onConfirm: async () => {
        try {
          await authenticatedApiCall(`/my-listings/${listingId}`, { method: 'DELETE' });
          
          // console.log('Success: Listing deleted successfully');
          fetchData();
          closeModal();
        } catch (err) {
          // console.error('❌ Delete listing error:', err);
          alert('Failed to delete listing: ' + err.message);
        }
      }
    });
  };

  const checkProfileComplete = () => {
    // Updated to check geographic data instead of institution data
    if (!user?.state_id || !user?.lga_id || !user?.lcda_id) {
      showConfirmation({
        title: 'Incomplete Profile',
        message: 'Please complete your location details in your profile before creating listings.',
        confirmText: 'Complete Profile',
        cancelText: 'Not Now',
        onConfirm: () => {
          navigate('/profile');
          closeModal();
        }
      });
      return false;
    }
    return true;
  };

  // New function to handle multiple listing actions
  const getListingActions = (listing) => {
    const actions = [];
    
    switch (listing.status) {
      case 'active':
        actions.push(
          {
            label: 'Edit',
            onClick: () => handleEditListing(listing.listing_id),
            style: 'default'
          },
          {
            label: 'Mark as Sold',
            onClick: () => handleMarkAsSold(listing.listing_id),
            style: 'default'
          },
          {
            label: 'Delete',
            onClick: () => handleDeleteListing(listing.listing_id, listing.title),
            style: 'destructive'
          }
        );
        break;
        
      case 'sold':
        actions.push(
          {
            label: 'View Details',
            onClick: () => navigate(`/listing/${listing.listing_id}`),
            style: 'default'
          },
          {
            label: 'Reactivate',
            onClick: () => handleReactivate(listing.listing_id),
            style: 'default'
          }
        );
        break;
        
      case 'draft':
        actions.push(
          {
            label: 'Edit',
            onClick: () => handleEditListing(listing.listing_id),
            style: 'default'
          },
          {
            label: 'Delete',
            onClick: () => handleDeleteListing(listing.listing_id, listing.title),
            style: 'destructive'
          }
        );
        break;
        
      case 'inactive':
        actions.push(
          {
            label: 'Reactivate',
            onClick: () => handleReactivate(listing.listing_id),
            style: 'default'
          },
          {
            label: 'Delete',
            onClick: () => handleDeleteListing(listing.listing_id, listing.title),
            style: 'destructive'
          }
        );
        break;
        
      default:
        actions.push(
          {
            label: 'Edit',
            onClick: () => handleEditListing(listing.listing_id),
            style: 'default'
          },
          {
            label: 'Delete',
            onClick: () => handleDeleteListing(listing.listing_id, listing.title),
            style: 'destructive'
          }
        );
    }
    
    return actions;
  };

  // Function to handle quick status updates
  const handleQuickStatusUpdate = async (listingId, newStatus) => {
    const statusMessages = {
      'active': 'activate',
      'sold': 'mark as sold',
      'inactive': 'deactivate',
      'draft': 'save as draft'
    };

    showConfirmation({
      title: 'Update Status',
      message: `Are you sure you want to ${statusMessages[newStatus] || 'update'} this listing?`,
      confirmText: 'Yes, Update',
      onConfirm: async () => {
        try {
          await authenticatedApiCall(`/my-listings/${listingId}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status: newStatus }),
          });
          
          // console.log(`Success: Listing ${statusMessages[newStatus] || 'updated'} successfully`);
          fetchData();
          closeModal();
        } catch (err) {
          // console.error('❌ Status update error:', err);
          alert('Failed to update listing status: ' + err.message);
        }
      }
    });
  };

  // Modal Component (to be rendered in parent component)
  const ConfirmationModalComponent = () => (
    <ConfirmationModal
      isOpen={modalConfig.isOpen}
      onClose={closeModal}
      onConfirm={modalConfig.onConfirm}
      title={modalConfig.title}
      message={modalConfig.message}
      confirmText={modalConfig.confirmText}
      cancelText={modalConfig.cancelText}
      confirmColor={modalConfig.confirmColor}
    />
  );

  return {
    handleEditListing,
    handleMarkAsSold,
    handleReactivate,
    handleDeleteListing,
    checkProfileComplete,
    getListingActions,
    handleQuickStatusUpdate,
    authenticatedApiCall,
    ConfirmationModalComponent,
    closeModal // Export if needed to close modal from outside
  };
};